import type { NodeViewProps } from "@tiptap/core";
import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";

interface AiBlockNode {
  attrs: {
    id?: string;
    placeholder?: string;
    range?: { from: number; to: number };
  };
}

export interface AiBlockContextValue {
  cleanupNode: () => void;
  deleteNode: NodeViewProps["deleteNode"];
  editor: NodeViewProps["editor"];
  getPos: NodeViewProps["getPos"];
  isLoadingResponse: boolean;
  node: AiBlockNode;
  setIsLoadingResponse: (value: boolean) => void;
}

const AiBlockContext = createContext<AiBlockContextValue | null>(null);
AiBlockContext.displayName = "AiBlockContext";

export const useAiBlockContext = (): AiBlockContextValue => {
  const context = useContext(AiBlockContext);
  if (!context) {
    throw new Error(
      "useAiBlockContext must be used within an AiBlockContextProvider",
    );
  }
  return context;
};

interface AiBlockContextProviderProps {
  children: ReactNode;
  deleteNode: NodeViewProps["deleteNode"];
  editor: NodeViewProps["editor"];
  getPos: NodeViewProps["getPos"];
  node: AiBlockNode;
}

export const AiBlockContextProvider = ({
  children,
  editor,
  node,
  getPos,
  deleteNode,
}: AiBlockContextProviderProps) => {
  const { range } = node.attrs;
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);

  const cleanupNode = () => {
    if (range) {
      editor.commands.setTextSelection({
        from: 0,
        to: editor.state.doc.content.size,
      });
      editor.chain().focus().unsetHighlight().run();
    }
    const position = getPos();
    if (position !== undefined) editor.commands.setTextSelection(position + 1);
    deleteNode();
  };

  const value: AiBlockContextValue = {
    editor,
    node,
    getPos,
    deleteNode,
    isLoadingResponse,
    setIsLoadingResponse,
    cleanupNode,
  };

  return (
    <AiBlockContext.Provider value={value}>{children}</AiBlockContext.Provider>
  );
};
